import type {
  BackendMetricConfig,
  BackendNotifierConfig,
  BackendScheduleConfig,
  BackendSubscriptionCreateRequest,
  MetricDefinition,
  NotificationTestRequest,
  SubscriptionFormData,
  SubscriptionV2Variable,
  TestNotifier,
} from "@/pages/subscriptions/services/types";
import {
  ChannelType,
  SubscriptionStatus,
  SubscriptionType,
  VariableType,
} from "@/pages/subscriptions/constants";
/**
 * Extract entity_type from variables
 */
function extractEntityType(variables: SubscriptionV2Variable[]): string {
  const entityVar = variables.find(v => v.type === VariableType.ENTITY);

  if (!entityVar) {
    throw new Error("Entity variable not found in variables");
  }

  // Extract entity type from the value (e.g., "all_warehouses" -> "warehouse")
  const value = String(entityVar.value);
  const match = value.match(/^all_(.+?)s?$/);
  return match ? match[1] : value;
}

function resolveRequiredPlatform(formData: SubscriptionFormData): string {
  const rawPlatform = formData.platform || formData.source_name || "";
  if (!rawPlatform || rawPlatform === "default") {
    throw new Error("Subscription platform is required");
  }
  return rawPlatform;
}

function resolveOptionalPlatform(formData: SubscriptionFormData): string | undefined {
  const rawPlatform = formData.platform || formData.source_name || "";
  if (!rawPlatform || rawPlatform === "default") {
    return undefined;
  }
  return rawPlatform;
}

/**
 * Resolve the platform a save request should carry, falling back to the
 * selected metric's ``source_name`` when the form hasn't captured one
 * (e.g. legacy edit-mode rows where ``EntityDetailsCard`` never fired).
 * Returns ``null`` when no usable platform can be derived — callers
 * surface a user-facing error rather than letting the request through
 * with a missing or sentinel ``"default"`` platform.
 */
export function resolvePlatformWithMetricFallback(
  data: SubscriptionFormData,
  metricsData: MetricDefinition[] | undefined
): string | null {
  if (data.platform && data.platform !== "default") {
    return data.platform;
  }
  const metricDef = metricsData?.find(m => m.name === data.metric);
  const sourceName = metricDef?.source_name;
  if (sourceName && sourceName !== "default") {
    return sourceName;
  }
  return null;
}

/**
 * Build metric_config from metric-related variables and filters
 */
function buildMetricConfig(
  variables: SubscriptionV2Variable[],
  subscriptionType: SubscriptionType,
  filters?: Array<{ dimension: string; value: string[] }>,
  category?: string
): BackendMetricConfig {
  const metricVar = variables.find(v => v.type === VariableType.METRIC);
  const aggregationVar = variables.find(v => v.type === VariableType.AGGREGATION);
  const changeTypeVar = variables.find(v => v.type === VariableType.CHANGE_TYPE);
  const comparisonVar = variables.find(v => v.type === VariableType.COMPARISON);
  const thresholdVar = variables.find(v => v.type === VariableType.THRESHOLD);
  const dateRangeVar = variables.find(v => v.type === VariableType.DATE_RANGE);

  if (!metricVar) {
    throw new Error("Metric variable not found in variables");
  }

  const metric_name = String(metricVar.value);
  const params: Record<string, string | number | boolean | undefined> = {};

  // Add aggregation if present
  if (aggregationVar && aggregationVar.value) {
    params.aggregation = String(aggregationVar.value);
  }

  // For alerts, add comparison-related params
  if (subscriptionType === SubscriptionType.ALERT) {
    if (changeTypeVar && changeTypeVar.value) {
      params.comparison_type = String(changeTypeVar.value);
    }

    if (comparisonVar && comparisonVar.value) {
      params.threshold_type = String(comparisonVar.value);
    }

    if (thresholdVar && thresholdVar.value) {
      const thresholdValue = String(thresholdVar.value);
      const isPercentage = thresholdValue.includes("%");
      const cleanValue = thresholdValue.replace(/[%,]/g, "");
      params.threshold_value = parseFloat(cleanValue);
      params.is_percentage = isPercentage;
    }

    if (dateRangeVar && dateRangeVar.value) {
      params.date_range = String(dateRangeVar.value);
    }
  }

  // Add filters as a separate array with proper structure
  const filtersArray =
    filters && filters.length > 0
      ? filters
          .filter(filter => filter.dimension && filter.value.length > 0)
          .map((filter, index) => ({
            order: index,
            key: filter.dimension,
            value: filter.value,
          }))
      : undefined;

  return {
    metric_name,
    ...(category ? { category } : {}),
    params,
    filters: filtersArray,
  };
}

/**
 * Convert schedule variable to cron expression
 */
function buildScheduleConfig(variables: SubscriptionV2Variable[]): BackendScheduleConfig {
  const scheduleVar = variables.find(v => v.type === VariableType.SCHEDULE);

  if (!scheduleVar) {
    throw new Error("Schedule variable not found in variables");
  }

  // Parse the JSON schedule value
  let scheduleData: {
    frequency: "daily" | "weekly" | "monthly";
    time: string;
    daysOfWeek?: string[];
    dayOfMonth?: number;
  };

  try {
    scheduleData = JSON.parse(String(scheduleVar.value));
  } catch {
    throw new Error("Invalid schedule format");
  }

  // Convert time to 24-hour format
  const timeParts = scheduleData.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeParts) {
    throw new Error("Invalid time format");
  }

  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const period = timeParts[3].toUpperCase();

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  // Build cron expression based on frequency
  let cron: string;

  if (scheduleData.frequency === "daily") {
    cron = `${minutes} ${hours} * * *`;
  } else if (scheduleData.frequency === "weekly") {
    const daysOfWeek = scheduleData.daysOfWeek || ["Mon"];
    const dayNumbers = daysOfWeek.map(day => {
      const dayMap: Record<string, number> = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
      };
      return dayMap[day] ?? 1;
    });
    cron = `${minutes} ${hours} * * ${dayNumbers.join(",")}`;
  } else if (scheduleData.frequency === "monthly") {
    const dayOfMonth = scheduleData.dayOfMonth || 1;
    cron = `${minutes} ${hours} ${dayOfMonth} * *`;
  } else {
    cron = `${minutes} ${hours} * * *`; // Default to daily
  }

  return {
    cron,
    timezone: "UTC",
  };
}

/**
 * Transform destination_config to notifier_config array
 */
function transformDestinationToNotifiers(formData: SubscriptionFormData): BackendNotifierConfig[] {
  const notifiers: BackendNotifierConfig[] = [];

  // Add email notifiers
  if (formData.channels.includes(ChannelType.EMAIL) && formData.email_recipients.length > 0) {
    notifiers.push({
      type: ChannelType.EMAIL,
      recipients: formData.email_recipients,
    });
  }

  // Add slack notifiers (one per channel)
  if (formData.channels.includes(ChannelType.SLACK) && formData.slack_channels.length > 0) {
    formData.slack_channels.forEach(channel => {
      notifiers.push({
        type: ChannelType.SLACK,
        channel: channel,
      });
    });
  }

  return notifiers;
}

/**
 * Main transformation function: Convert frontend form data to backend request format
 */
export function transformToBackendPayload(
  formData: SubscriptionFormData
): BackendSubscriptionCreateRequest {
  const variables = formData.variables || [];
  const filters = formData.filters || [];

  // Extract entity type from variables
  const entity_type = extractEntityType(variables);

  // Build metric configuration with filters and category
  const metric_config = buildMetricConfig(variables, formData.rule_type, filters, formData.source);

  // Build schedule configuration
  const schedule_config = buildScheduleConfig(variables);

  // Transform destination to notifiers
  const notifiers = transformDestinationToNotifiers(formData);

  // Fail closed: a missing or sentinel "default" platform must NOT silently fall back
  // to snowflake. Doing so would route a Databricks subscription to a Snowflake metric
  // and produce confusing rule-creation failures downstream.
  const platform = resolveRequiredPlatform(formData);

  // Build the backend payload with delivery object
  return {
    name: formData.name,
    rule_type: formData.rule_type,
    platform,
    entity_type,
    metric_config,
    delivery: {
      schedule: schedule_config,
      notifiers,
    },
    status: SubscriptionStatus.ACTIVE,
  };
}

/**
 * Build a request payload for the notification test endpoint.
 * Mirrors the create-rule body but drops delivery and substitutes a single notifier.
 */
export function buildTestNotificationPayload(
  formData: SubscriptionFormData,
  notifier: TestNotifier
): NotificationTestRequest {
  const variables = formData.variables || [];
  const filters = formData.filters || [];
  const entity_type = extractEntityType(variables);
  const metric_config = buildMetricConfig(variables, formData.rule_type, filters, formData.source);

  const platform = resolveOptionalPlatform(formData);
  return {
    name: formData.name,
    rule_type: formData.rule_type,
    ...(platform ? { platform } : {}),
    entity_type,
    metric_config,
    notifier,
  };
}

/**
 * Returns null when the form has the prerequisites to build a test request,
 * or a user-facing reason string explaining what's missing.
 *
 * Why: the Send-test button needs the same metric_config completeness as Save,
 * but reaching this state without going through Customize would throw inside
 * extractEntityType / buildMetricConfig. The predicate avoids that.
 */
export function getTestPayloadBlocker(formData: SubscriptionFormData): string | null {
  if (!formData.name?.trim()) {
    return "Add a subscription name first.";
  }
  if (!formData.metric || !formData.source) {
    return "Pick a category and metric first.";
  }
  const variables = formData.variables || [];
  const hasEntity = variables.some(v => v.type === VariableType.ENTITY);
  const hasMetricVar = variables.some(v => v.type === VariableType.METRIC);
  if (!hasEntity || !hasMetricVar) {
    return "Finish customizing the subscription first.";
  }
  return null;
}
